<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { ref, watch } from "vue";
import { v4 as uuid } from "uuid";

import { PANEL_SIZE } from "../../constants";

import { caseFileService } from "@/services";
import { sleep, isNumber } from "@/services/UtilitiesService";

import type { PredefinedSearch } from "@/models/SearchRequest";
import type { SearchRequest, SearchResult } from "@/models/Viewer";

interface SearchOption {
  label: string;
  value: string;
  supported: string[];
}
const emit = defineEmits<{
  (e: "search", results: SearchResult[]): void;
  (e: "selectSearch", result: SearchResult): void;
}>();
withDefaults(defineProps<{
  predefinedSearch: PredefinedSearch;
  searchResults: SearchResult[];
  selectedSearch: SearchResult | undefined;
  session: string;
}>(), {  })

const searchInput = ref<string>("");
let error = _ref<string>("");
let searchStatus = _ref<"IDLE" | "LOADING_REQUEST" | "LOADING_RESULTS" | "CANCELLED" | "ERROR">("IDLE");

const searchOptions: SearchOption[] = [
  {
    label: "Exact Phrase",
    value: "exactPhrase",
    supported: ["input", "matchCase"],
  },
  {
    label: "Proximity Search",
    value: "proximitySearch",
    supported: ["proximity", "input", "matchCase"],
  },
  {
    label: "Match Any Word",
    value: "anyWord",
    supported: ["input", "matchCase", "wildcard"],
  },
  {
    label: "Regular Expression",
    value: "regex",
    supported: ["input", "matchCase"],
  },
  {
    label: "Predefined Search",
    value: "predefinedSearch",
    supported: ["predefinedSearch"],
  },
];
const selectedOption = ref<SearchOption>(searchOptions[0]);
const proximity = ref<number | null>(null);
let selectedPredefinedSearch = _ref<PredefinedSearch["terms"][number] | null>(null);
const matchCase = ref<boolean>(false);
const wildcard = ref<boolean>(false);

const selectedSearchId = _computed(() => {
  return __props.selectedSearch?.id || null;
});
const selectedSearchIndex = _computed(() => {
  return __props.searchResults.findIndex((result) => result.id === selectedSearchId.value);
});

async function search() {
  if (/LOADING.*/.test(searchStatus.value)) return;

  searchStatus.value = "LOADING_REQUEST";
  // Reset error message
  error.value = "";
  emit("search", []);
  const option = selectedOption.value;

  if (option.value === "predefinedSearch") {
    if (!selectedPredefinedSearch.value) {
      searchStatus.value = "IDLE";
      return;
    }

    let pattern: string;
    const { beginsWith, endsWith, matchCase: caseSensitive, matchWholeWord } = selectedPredefinedSearch.value.options;

    if (!selectedPredefinedSearch.value.searchTermIsRegex) {
      pattern = [
        matchWholeWord || beginsWith ? "\\b" : "",
        selectedPredefinedSearch.value.searchTerm,
        matchWholeWord || endsWith ? "\\b" : "",
      ].join("");
    } else pattern = selectedPredefinedSearch.value.userDefinedRegex;

    const searchReq = await buildSearchRequest({
      searchTerms: [{ type: "simple", pattern, caseSensitive }],
    });

    return loadResults(searchReq);
  }

  if (!searchInput.value) {
    error.value = "Please enter a search query to continue";
    searchStatus.value = "ERROR";
    return;
  }

  if (option.value === "proximitySearch") {
    if (searchInput.value.split(" ").length < 2) {
      error.value = "You must specify at least two search terms in a proximity search";
      searchStatus.value = "ERROR";
      return;
    }

    if (!proximity.value) {
      error.value = "You must specify the proximity between search terms";
      searchStatus.value = "ERROR";
      return;
    }

    // Not predefined search, so reset selected value of predefined search
    selectedPredefinedSearch.value = null;
    const proximitySearchRequest = await buildSearchRequest({
      searchTerms: [
        {
          type: "proximity",
          distance: proximity.value ? +proximity.value : 0,
          subTerms: searchInput.value.split(" ").map((i) => ({ pattern: i, caseSensitive: matchCase.value })),
        },
      ],
    });
    return loadResults(proximitySearchRequest);
  }

  // Not predefined search, so reset selected value of predefined search
  selectedPredefinedSearch.value = null;
  const searchRequest = await buildSearchRequest({
    searchTerms: [
      {
        pattern: searchInput.value,
        type: "simple",
        caseSensitive: matchCase.value,
      },
    ],
  });
  return loadResults(searchRequest);
}

async function buildSearchRequest(payload: SearchRequest) {
  try {
    return await caseFileService.searchDocument(__props.session, payload);
  } catch (e) {
    searchStatus.value = "ERROR";
    error.value = "Error occurred. Please try again later";
    console.error("error", e);
  }
}

async function loadResults(searchRequest: any) {
  if (/LOADING_RESULTS|CANCELLED/.test(searchStatus.value)) return;

  const sortFn = (a: SearchResult, b: SearchResult) => {
    const index = a.pageIndex - b.pageIndex;
    const x = a.boundingRectangle.x - b.boundingRectangle.x;
    const y = a.boundingRectangle.y - b.boundingRectangle.y;

    return index !== 0 ? index : y !== 0 ? y : x;
  };

  searchStatus.value = "LOADING_RESULTS";
  try {
    let results;
    const pid = searchRequest.processId as string;
    const response = await caseFileService.getSearchResults(pid);

    results = [...response.results.map((r) => ({ ...r, id: uuid() }))];
    results.sort(sortFn);
    emit("search", results);

    let { continueToken, continueAfter } = response;
    while (continueToken && searchStatus.value === "LOADING_RESULTS") {
      await sleep(continueAfter!);
      const newResponse = await caseFileService.getSearchResults(pid, continueToken);
      continueToken = newResponse.continueToken;
      continueAfter = newResponse.continueAfter;
      results = [...results, ...newResponse.results.map((r) => ({ ...r, id: uuid() }))];
      results.sort(sortFn);
      emit("search", results);
    }
    searchStatus.value = "IDLE";
  } catch (e) {
    searchStatus.value = "ERROR";
    error.value = "Error occurred. Please try again later";
  }
}
function cancel() {
  if (!/LOADING.*/.test(searchStatus.value)) return;
  searchStatus.value = "CANCELLED";
}

function next() {
  if (!__props.searchResults.length) {
    return;
  }
  const currentIndex = selectedSearchIndex.value;
  if (currentIndex + 1 >= __props.searchResults.length) {
    return;
  }
  const result = __props.searchResults[currentIndex + 1];
  emit("selectSearch", result);
}

function prev() {
  if (!__props.searchResults.length) {
    return;
  }
  const currentIndex = selectedSearchIndex.value;
  if (currentIndex - 1 < 0) {
    return;
  }
  const result = __props.searchResults[currentIndex - 1];
  emit("selectSearch", result);
}

function first() {
  if (!__props.searchResults.length) return;
  emit("selectSearch", __props.searchResults[0]);
}
function last() {
  if (!__props.searchResults.length) return;
  const result = __props.searchResults[__props.searchResults.length - 1];
  emit("selectSearch", result);
}

// Reset variables when option changes
watch(selectedOption, () => {
  error.value = "";
  searchInput.value = "";
  selectedPredefinedSearch.value = null;
  matchCase.value = false;
  wildcard.value = false;
});
</script>
<template>
  <div :style="{ width: PANEL_SIZE + 'px' }" class="search-panel tw-flex tw-flex-col tw-shadow">
    <select v-model="selectedOption" class="tw-form-select tw-flex-shrink-0">
      <option v-for="option in searchOptions" :key="option.value" :value="option">
        {{ option.label }}
      </option>
    </select>
    <input
      v-if="selectedOption.supported.indexOf('input') !== -1"
      v-model="searchInput"
      class="tw-form-input tw-my-1"
      type="text"
      autofocus
      placeholder="Enter search query"
      @keyup.enter="search"
    />
    <input
      v-if="selectedOption.supported.indexOf('proximity') !== -1"
      v-model="proximity"
      type="number"
      class="tw-form-input tw-mb-1"
      placeholder="Max word distances"
      @keypress="isNumber($event)"
      @keyup.enter="search"
    />
    <div v-if="selectedOption.supported.indexOf('matchCase') !== -1">
      <input v-model="matchCase" type="checkbox" /> Case sensitive
    </div>
    <div v-if="selectedOption.supported.indexOf('wildcard') !== -1">
      <input v-model="wildcard" type="checkbox" /> Use wildcards
    </div>
    <select
      v-if="selectedOption.supported.indexOf('predefinedSearch') !== -1"
      v-model="selectedPredefinedSearch"
      class="tw-form-select tw-mt-1 tw-flex-shrink-0"
      @change="search"
    >
      <option selected :value="null">Select search term</option>
      <option v-for="option in predefinedSearch.terms" :key="option.description" :value="option">
        {{ option.description }}
      </option>
    </select>
    <div class="no-results">
      <div v-if="/LOADING.*/.test(searchStatus)" class="tw-px-1 tw-py-2">
        Loading search results...
        <span class="cancel tw-font-medium" @click="cancel">Cancel</span>
      </div>
      <div v-else-if="searchStatus === 'CANCELLED'" class="tw-px-1 tw-py-2">Search cancelled.</div>
      <div v-else-if="searchStatus === 'ERROR'" class="tw-px-1 tw-py-2">
        {{ error }}
      </div>
      <div v-else-if="searchResults.length === 0" class="tw-px-1 tw-py-2">No results found</div>
    </div>
    <div v-if="searchResults.length" class="tw-flex tw-h-6 tw-justify-between tw-text-lg">
      <div>
        <i
          class="fa fa-backward tw-mx-1 tw-cursor-pointer tw-p-1 tw-shadow-inner"
          title="First result"
          @click="first()"
        />
      </div>
      <div>
        <i
          class="fa fa-caret-up fa-lg tw-mx-1 tw-cursor-pointer tw-p-1 tw-shadow-inner"
          title="Previous result"
          @click="prev()"
        />
        <i
          class="fa fa-caret-down fa-lg tw-mx-1 tw-cursor-pointer tw-p-1 tw-shadow-inner"
          title="Next result"
          @click="next()"
        />
      </div>
      <div>
        <i class="fa fa-forward tw-mx-1 tw-cursor-pointer tw-p-1 tw-shadow-inner" title="Last result" @click="last()" />
      </div>
    </div>
    <div v-if="searchResults.length" id="results" class="results tw-mt-2">
      <div
        v-for="(result, index) in searchResults"
        :id="result.index"
        :key="result.index"
        class="search-result"
        :class="{
          odd: index % 2 === 1 && selectedSearchId !== result.id,
          'tw-bg-blue-100': selectedSearchId === result.id,
        }"
        @click="$emit('selectSearch', result)"
      >
        ...
        <template v-for="(text, textIdx) in result.context.split(result.text)" :key="textIdx">
          <span>
            {{ text }}
          </span>
          <span v-if="textIdx + 1 < result.context.split(result.text).length" style="color: blue">
            {{ result.text }}
          </span>
        </template>
        ...
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.search-panel {
  padding: 12px 8px 2px 8px;
}

input {
  line-height: 13px;
}

.results-header {
  background: #2b1b4f;
  color: white;
  font-size: 1rem;
  font-weight: 500;
  padding: 2px 4px;
  margin-top: 16px;
}
.results {
  border: 1px solid #dae1e7;
  background: #f2f3f6;
  overflow: auto;
  min-height: 0;

  .search-result {
    padding: 2px 4px;
    cursor: pointer;

    &.odd {
      background: white;
    }
    &.selected {
      background: #d3def4;
    }
  }
}
.no-results {
  .cancel {
    cursor: pointer;
  }
}
</style>
